import React from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@shared/ui/dialog";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useAddFaqs, useUpdateFaqs, useFaqCategory } from "../../hooks";
import { useUtilityServices } from "@features/mx/readCycleManagement/hooks"; // Import utility hook
import { CreateFAQPayload, UpdateFAQPayload } from "../../types";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

interface FAQ {
  id: string;
  question: string;
  answer: string;
  category: string;
  utility: string;
  status: "active" | "inactive";
  order: number;
}

interface FAQDialogProps {
  faq: FAQ | null;
  utilities: string[];
  onSave: (faq: Partial<FAQ>) => void;
  onCancel: () => void;
  onRefetch?: () => void; // Add refetch callback
}

export const FAQFormDialog = ({ faq, utilities, onSave, onCancel, onRefetch }: FAQDialogProps) => {
  const remoteUtilityId = getRemoteUtilityId();
  const { mutate: addFaq, isPending: isCreating } = useAddFaqs();
  const { mutate: updateFaq, isPending: isUpdating } = useUpdateFaqs();
  
  // Fetch categories from API
  const { data: categoriesData, isLoading: isCategoriesLoading } = useFaqCategory({
    remote_utility_id: remoteUtilityId
  });

  // Fetch utilities from API
  const { data: utilitiesData } = useUtilityServices();
  
  // Transform categories data
  const categories = categoriesData?.result?.map(cat => ({
    label: cat.value,
    value: cat.value // Use cat.value instead of cat.key for consistency
  })) || [];

  // Get utility services from API
  const apiUtilities = utilitiesData
    ? utilitiesData
        .filter((utility) => utility.isActive === true)
        .map((utility) => utility.name)
    : [];

  // Use API utilities if available, otherwise fallback to passed utilities
  const availableUtilities = apiUtilities.length > 0 ? apiUtilities : utilities;
  
  // Determine if we're in edit mode and get the appropriate loading state
  const isEditMode = !!faq;
  const isPending = isEditMode ? isUpdating : isCreating;

  // Create form fields configuration
  const formFields: FormField[] = [
    {
      name: "question",
      type: "text",
      label: "Question",
      required: true,
      placeholder: "Enter the question...",
      helperText: "Write the question exactly as users would ask it - be clear and specific",
      classes: {
        container: "mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input: "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "answer",
      type: "textarea",
      label: "Answer",
      required: true,
      placeholder: "Enter the answer...",
      helperText: "Provide a comprehensive answer with step-by-step instructions when applicable",
      classes: {
        container: "mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        input: "w-full min-h-24 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "utility",
      type: "select",
      label: "Utility",
      required: true,
      options: availableUtilities.map(utility => ({
        label: utility,
        value: utility
      })),
      placeholder: "-- Select Utility --",
      helperText: "Select the utility service this FAQ relates to",
      classes: {
        container: "mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select: "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
    {
      name: "category",
      type: "select",
      label: "Category",
      required: true,
      options: categories,
      placeholder: isCategoriesLoading ? "Loading categories..." : "-- Select Category --",
      helperText: "Choose the most relevant category to help users find this FAQ easily",
      disabled: isCategoriesLoading,
      classes: {
        container: "mb-4",
        label: "block text-sm font-medium text-gray-700 mb-2",
        select: "w-full h-12 px-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500",
        error: "text-red-600 text-sm mt-1",
        helperText: "text-xs text-gray-500 mt-1",
      },
    },
  ];

  // Create form configuration
  const formConfig = {
    fields: formFields,
    mode: "onBlur" as const,
  };

  // Create form using FormService
  const form = FormService.createForm(formConfig);

  // Set initial values for edit mode - only when all required data is available
  React.useEffect(() => {
    if (faq && categories.length > 0 && availableUtilities.length > 0) {
      const initialValues = {
        question: faq.question || "",
        answer: faq.answer || "",
        utility: faq.utility || "",
        category: faq.category || "",
      };
      
      // Use setValue instead of reset to avoid clearing user input
      Object.keys(initialValues).forEach(key => {
        form.setValue(key as keyof typeof initialValues, initialValues[key as keyof typeof initialValues]);
      });
    }
  }, [faq, categories.length, availableUtilities.length, form]);

  function handleSubmit(data: any) {
    if (isEditMode) {
      // Updating existing FAQ - transform data to update API payload structure
      const updatePayload: UpdateFAQPayload = {
        utility_service: data.utility,
        name: data.question,
        answer: data.answer,
        code: faq.id, // Use existing code
        remote_utility_id: remoteUtilityId,
        faq_category: data.category,
      };

      updateFaq(updatePayload, {
        onSuccess: () => {
          onRefetch?.(); // Refetch data after successful update
          onSave(data); // Call the parent's onSave for any additional handling
        },
        onError: (error) => {
          console.error('Failed to update FAQ:', error);
        }
      });
    } else {
      // Creating new FAQ - transform data to API payload structure
      const createPayload: CreateFAQPayload = {
        utility_service: data.utility,
        name: data.question,
        answer: data.answer,
        code: data.question,
        remote_utility_id: remoteUtilityId,
        faq_category: data.category
      };

      addFaq(createPayload, {
        onSuccess: () => {
          onRefetch?.(); // Refetch data after successful creation
          onSave(data); // Call the parent's onSave for any additional handling
        },
        onError: (error) => {
          console.error('Failed to create FAQ:', error);
        }
      });
    }
  }

  function handleCancel() {
    form.reset();
    onCancel();
  }

  // Custom footer with cancel button
  const renderCustomFooter = () => (
    <div className="flex gap-3 pt-6 border-t border-gray-200">
      <button
        type="button"
        onClick={handleCancel}
        className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
        disabled={isPending}
      >
        Cancel
      </button>
      <button
        type="button"
        onClick={() => form.handleSubmit(handleSubmit)()}
        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
        disabled={isPending || isCategoriesLoading}
      >
        {isPending ? (isEditMode ? "Updating..." : "Creating...") : (isEditMode ? "Update" : "Create")} FAQ
      </button>
    </div>
  );

  return (
    <Dialog open={true} onOpenChange={handleCancel}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? "Edit FAQ" : "Add New FAQ"}</DialogTitle>
          <DialogDescription>
            {isEditMode ? "Update the FAQ details below." : "Create a new frequently asked question."}
          </DialogDescription>
        </DialogHeader>
        
        <div id="faq-dynamic-form">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            config={{
              ...formConfig,
              showSubmitButton: false, 
            }}
            renderCustomFooter={renderCustomFooter}
            gridColumns={1}
            responsiveColumns={{
              md: 2, 
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};